import React, { useEffect, useMemo, useState } from "react";
import { Flame, Plus, Save, RefreshCw, Power, PowerOff } from "lucide-react";

async function apiGet(url) {
    const r = await fetch(url, { credentials: "include" });
    const j = await r.json().catch(() => null);
    if (!j || j.success !== true) throw new Error(j?.error || "Errore API");
    return j;
}

async function apiPost(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => null);
    if (!j || j.success !== true) throw new Error(j?.error || "Errore API");
    return j;
}

function emptyForm(defaultAziendaId = "") {
    return {
        id: "",
        azienda_id: defaultAziendaId,
        codice: "",
        nome: "",
        ordinamento: 0,
        attiva: 1,
    };
}

export default function FriggitriciAdmin({ me, isSuper }) {
    const aziendaIdFromMe = String(me?.azienda_id || "");
    const [aziende, setAziende] = useState([]);
    const [selectedAziendaId, setSelectedAziendaId] = useState(
        isSuper ? "" : aziendaIdFromMe
    );

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [okMsg, setOkMsg] = useState("");

    const [form, setForm] = useState(emptyForm(isSuper ? "" : aziendaIdFromMe));

    const effectiveAziendaId = useMemo(() => {
        return isSuper ? selectedAziendaId : aziendaIdFromMe;
    }, [isSuper, selectedAziendaId, aziendaIdFromMe]);

    useEffect(() => {
        if (isSuper) loadAziende();
    }, [isSuper]);

    useEffect(() => {
        if (effectiveAziendaId) {
            loadItems();
            setForm((prev) => ({
                ...prev,
                azienda_id: effectiveAziendaId,
            }));
        } else {
            setItems([]);
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveAziendaId]);

    async function loadAziende() {
        try {
            const j = await apiGet("/api/admin/aziende_list.php");
            setAziende(Array.isArray(j.records) ? j.records : []);
        } catch (e) {
            console.error(e);
        }
    }

    async function loadItems() {
        if (!effectiveAziendaId) return;
        setLoading(true);
        setErr("");
        setOkMsg("");
        try {
            const qs = `?azienda_id=${encodeURIComponent(effectiveAziendaId)}`;
            const j = await apiGet(`/api/admin/friggitrici_list.php${qs}`);
            setItems(Array.isArray(j.records) ? j.records : []);
        } catch (e) {
            setErr(e.message || "Errore caricamento friggitrici");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }

    function resetForm() {
        setForm(emptyForm(effectiveAziendaId || ""));
    }

    function startEdit(row) {
        setErr("");
        setOkMsg("");
        setForm({
            id: String(row.id || ""),
            azienda_id: String(row.azienda_id || effectiveAziendaId || ""),
            codice: row.codice || "",
            nome: row.nome || "",
            ordinamento: Number(row.ordinamento || 0),
            attiva: Number(row.attiva ?? 1),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setErr("");
        setOkMsg("");

        if (!form.azienda_id) {
            setErr("Seleziona un'azienda.");
            return;
        }
        if (!form.codice.trim()) {
            setErr("Il codice è obbligatorio.");
            return;
        }
        if (!form.nome.trim()) {
            setErr("Il nome è obbligatorio.");
            return;
        }

        setSaving(true);
        try {
            await apiPost("/api/admin/friggitrici_save.php", {
                id: form.id ? Number(form.id) : null,
                azienda_id: Number(form.azienda_id),
                codice: form.codice.trim(),
                nome: form.nome.trim(),
                ordinamento: Number(form.ordinamento || 0),
                attiva: Number(form.attiva ? 1 : 0),
            });

            setOkMsg(form.id ? "Friggitrice aggiornata." : "Friggitrice creata.");
            resetForm();
            await loadItems();
        } catch (e2) {
            setErr(e2.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    async function toggleAttiva(row) {
        setErr("");
        setOkMsg("");
        try {
            await apiPost("/api/admin/friggitrici_save.php", {
                id: Number(row.id),
                azienda_id: Number(row.azienda_id),
                codice: row.codice,
                nome: row.nome,
                ordinamento: Number(row.ordinamento || 0),
                attiva: Number(row.attiva) ? 0 : 1,
            });
            await loadItems();
        } catch (e) {
            setErr(e.message || "Errore aggiornamento stato");
        }
    }

    const aziendaLabel = useMemo(() => {
        if (!isSuper) {
            return me?.ragione_sociale || me?.azienda_nome || "La tua azienda";
        }
        const found = aziende.find((a) => String(a.id) === String(selectedAziendaId));
        return found?.ragione_sociale || found?.nome || "";
    }, [isSuper, me, aziende, selectedAziendaId]);

    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-white shadow-sm border p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            <Flame size={20} />
                            Gestione Friggitrici
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Crea e gestisci le friggitrici disponibili per il modulo verifica olii.
                        </p>
                    </div>

                    <button
                        onClick={loadItems}
                        className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 bg-white hover:bg-slate-50"
                    >
                        <RefreshCw size={16} />
                        Aggiorna
                    </button>
                </div>

                {isSuper && (
                    <div className="mt-4 max-w-md">
                        <label className="block text-sm font-medium mb-1">Azienda</label>
                        <select
                            value={selectedAziendaId}
                            onChange={(e) => {
                                setSelectedAziendaId(e.target.value);
                                setForm(emptyForm(e.target.value));
                            }}
                            className="w-full rounded-xl border px-3 py-2 bg-white"
                        >
                            <option value="">-- Seleziona azienda --</option>
                            {aziende.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.ragione_sociale || a.nome || `Azienda #${a.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {!isSuper && (
                    <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">
                        Azienda corrente: <strong>{aziendaLabel}</strong>
                    </div>
                )}

                {err ? (
                    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        {err}
                    </div>
                ) : null}

                {okMsg ? (
                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                        {okMsg}
                    </div>
                ) : null}
            </div>

            <form onSubmit={handleSubmit} className="rounded-2xl bg-white shadow-sm border p-5">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <h3 className="text-lg font-semibold">
                        {form.id ? "Modifica friggitrice" : "Nuova friggitrice"}
                    </h3>

                    {form.id ? (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="rounded-xl border px-3 py-2 bg-white hover:bg-slate-50 text-sm"
                        >
                            Nuovo inserimento
                        </button>
                    ) : null}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {isSuper && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Azienda</label>
                            <select
                                value={form.azienda_id}
                                onChange={(e) => setForm((prev) => ({ ...prev, azienda_id: e.target.value }))}
                                className="w-full rounded-xl border px-3 py-2 bg-white"
                            >
                                <option value="">-- Seleziona --</option>
                                {aziende.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.ragione_sociale || a.nome || `Azienda #${a.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">Codice</label>
                        <input
                            value={form.codice}
                            onChange={(e) => setForm((prev) => ({ ...prev, codice: e.target.value }))}
                            placeholder="Es. FRY-01"
                            className="w-full rounded-xl border px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Nome</label>
                        <input
                            value={form.nome}
                            onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                            placeholder="Es. Friggitrice 1"
                            className="w-full rounded-xl border px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Ordinamento</label>
                        <input
                            type="number"
                            value={form.ordinamento}
                            onChange={(e) =>
                                setForm((prev) => ({ ...prev, ordinamento: Number(e.target.value || 0) }))
                            }
                            className="w-full rounded-xl border px-3 py-2"
                        />
                    </div>

                    <div className="flex items-end">
                        <label className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 bg-slate-50">
                            <input
                                type="checkbox"
                                checked={Number(form.attiva) === 1}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, attiva: e.target.checked ? 1 : 0 }))
                                }
                            />
                            <span className="text-sm">Attiva</span>
                        </label>
                    </div>
                </div>

                <div className="mt-5">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
                    >
                        {form.id ? <Save size={16} /> : <Plus size={16} />}
                        {saving ? "Salvataggio..." : form.id ? "Aggiorna friggitrice" : "Aggiungi friggitrice"}
                    </button>
                </div>
            </form>

            <div className="rounded-2xl bg-white shadow-sm border overflow-hidden">
                <div className="px-5 py-4 border-b bg-slate-50">
                    <h3 className="text-lg font-semibold">
                        Elenco friggitrici {aziendaLabel ? `— ${aziendaLabel}` : ""}
                    </h3>
                </div>

                {loading ? (
                    <div className="p-5 text-sm text-slate-500">Caricamento friggitrici…</div>
                ) : items.length === 0 ? (
                    <div className="p-5 text-sm text-slate-500">Nessuna friggitrice trovata.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-4 py-3 border-b">ID</th>
                                    <th className="text-left px-4 py-3 border-b">Codice</th>
                                    <th className="text-left px-4 py-3 border-b">Nome</th>
                                    <th className="text-left px-4 py-3 border-b">Ord.</th>
                                    <th className="text-left px-4 py-3 border-b">Stato</th>
                                    <th className="text-left px-4 py-3 border-b">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 border-b">{row.id}</td>
                                        <td className="px-4 py-3 border-b font-medium">{row.codice}</td>
                                        <td className="px-4 py-3 border-b">{row.nome}</td>
                                        <td className="px-4 py-3 border-b">{row.ordinamento}</td>
                                        <td className="px-4 py-3 border-b">
                                            {Number(row.attiva) ? (
                                                <span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-medium">
                                                    Attiva
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-full bg-slate-200 text-slate-700 px-2 py-1 text-xs font-medium">
                                                    Disattivata
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(row)}
                                                    className="rounded-lg border px-3 py-1.5 bg-white hover:bg-slate-50"
                                                >
                                                    Modifica
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleAttiva(row)}
                                                    className="rounded-lg border px-3 py-1.5 bg-white hover:bg-slate-50 inline-flex items-center gap-1"
                                                >
                                                    {Number(row.attiva) ? <PowerOff size={14} /> : <Power size={14} />}
                                                    {Number(row.attiva) ? "Disattiva" : "Attiva"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}